import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Formik, Form, validateYupSchema, yupToFormErrors } from 'formik';
import { Helmet } from 'react-helmet';

// CONSTANTS
import {
  ROUTE_START,
  ROUTE_PARTNER_CHANNELS,
  ROUTE_DISCOVER,
  ROUTE_RESULTS,
} from '@/constants/routes';
import { startInitFormData } from '@/constants/wizard';
import { LOCATIONS_CHANNELS_PAGE } from '@/constants/locations';

// UTILITY
import { getSearchParams, sendParentMessage } from '@/utility/common';
import setYupValidationSchema from '@/utility/validationSchema/validationSchema';
import {
  analyticsPageLoaded,
  analyticsLoadedQuestion,
  analyticsInternalLinks,
} from '@/utility/analytics';
import { pagePartnerProtector, routeEnhance } from '@/utility/routes';
import bugsnag from '@/utility/bugsnagClient';
import store from '@/utility/store';

// STORE
import { ChannelsActions, CountiesActions, WizardActions, PagesActions } from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import Guard from '@/hoc/Guard';
import withPrefetch from '@/hoc/withPrefetch';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import Redirect from '@/components/Redirect';
import HeaderWizard from '@/components/HeaderWizard';
import FooterWizard from '@/components/FooterWizard';
import DialogExitPopup from '@/components/Dialogs/DialogExitPopup';

// HELP
import ChannelsStep from './help/ChannelsStep';
import Email from './help/Email';
import Stepper from '@/components/Stepper/Stepper';

class Channels extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeStep: 1,
      startData: {},
      selectedChannels: [],
      isEditChannels: false,
      redirectToStart: false,
      initialFormData: {
        channels: [],
        selected_content: [],
        search: '',
        genre_search: props.filters[0] ? props.filters[0].genre_id.toString() : '',
        zipcode: '',
        dma_id: '',
        county_id_fk: '',
        email: '',
        terms: true,
        form_new: 1,
      },
    };
    this.form = React.createRef();
    this.params = getSearchParams(document.location.search);
  }

  componentDidMount() {
    const { initialFormData } = this.state;
    const {
      getChannelFilters,
      getChannels,
      getTopChannels,
      getFormData,
      router: { params, routerMatch },
      isIframe,
      getUniqueId,
      urlPartner,
      partner,
      isPartnerDomain,
      getSkipQuestions,
      getPagesContent,
      handlePrefetch,
    } = this.props;
    const startData = store.get('start-data') || null;
    const channelsData = store.get('channels-data') || null;
    const dma_id_fk = startData ? startData.dma_id : '';
    const sports = startData ? startData.sports : '';
    const county_id_fk = startData ? startData.county_id_fk : '';
    const isPartnerLink =
      routerMatch({ path: ROUTE_PARTNER_CHANNELS }) ||
      isPartnerDomain ||
      !!startData?.partner_id_fk;
    const hasPrefilled = this.params ? this.params.has('prefilled') : false;

    if (params.unique_id) {
      this.setState({
        isEditChannels: !hasPrefilled,
        initialFormData: { ...initialFormData, form_new: 0 },
      });
      getFormData(params.unique_id);
    } else {
      if (!!urlPartner && startData?.partner?.slug !== urlPartner) {
        this.setState({ redirectToStart: true });
        return;
      }

      if (isPartnerLink) {
        let slug = params.slug || params.partner;

        if (isPartnerDomain) {
          slug = urlPartner;
        }

        getSkipQuestions(slug);
      }

      if (startData) {
        this.setState({
          startData,
          initialFormData: { ...initialFormData, dma_id: dma_id_fk, county_id_fk },
        });

        if (!startData.unique_id) {
          getUniqueId(null, 'channels');
        }
      } else {
        getUniqueId(null, 'channels');
      }

      if (channelsData) {
        this.setState({
          selectedChannels: channelsData.selectedChannels,
          initialFormData: {
            ...initialFormData,
            channels: channelsData.channels,
            selected_content: channelsData.selected_content || [],
          },
        });
      }
    }

    const live_local_news = startData?.live_local_news === 'yes' ? { live_local_news: 'yes' } : {};

    getChannelFilters();
    getChannels({ dma_id_fk });
    getTopChannels({ dma_id_fk, sports, ...live_local_news });
    getPagesContent('channels');
    // prefetch next page
    handlePrefetch(ROUTE_RESULTS);

    if (isIframe) {
      const channelsLength = channelsData?.channels?.length;

      sendParentMessage({
        goToTop: true,
        fullHeight: true,
        showParentFooter:
          startData?.partner?.settings?.show_iframe_footer || partner?.settings?.show_iframe_footer,
        isEditChannels: !!params.unique_id,
        disableNext: !channelsLength,
      });
      window.addEventListener('message', this.receiveMessage, false);
    }

    document.addEventListener('keydown', this.handleKeyDown, { passive: true });

    analyticsLoadedQuestion({
      question_slug: 'channels',
      partner_extra: false,
      partner: startData?.partner?.slug || partner?.slug,
      partner_id: startData?.partner?.partner_id || partner?.partner_id,
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { activeStep } = this.state;
    const {
      user,
      filters,
      formData: storeFormData,
      getChannels,
      getTopChannels,
      unique_id,
      partner,
      isIframe,
    } = this.props;
    const formData = { ...storeFormData };
    const hasPrefilled = this.params ? this.params.has('prefilled') : false;

    pagePartnerProtector(partner, ['use_fmb'], ROUTE_DISCOVER);

    if (prevState.activeStep !== activeStep) {
      this.form.current.validateForm();

      switch (activeStep) {
        case 1:
          analyticsLoadedQuestion({
            question_slug: 'channels',
            partner_extra: false,
            partner: partner.slug,
            partner_id: partner.partner_id,
          });
          break;
        case 2:
          analyticsLoadedQuestion({
            question_slug: 'email',
            partner_extra: false,
            partner: partner.slug,
            partner_id: partner.partner_id,
          });
          break;

        default:
          break;
      }
    }

    if (prevProps.filters.length !== filters.length && filters.length) {
      this.setSelectedFilter(filters[0]);
    }

    if (!prevState.initialFormData.email && user && !formData?.email) {
      const { initialFormData } = this.state;

      this.setState({
        initialFormData: { ...initialFormData, email: user.email },
      });
    }

    if (prevProps.formData !== storeFormData && storeFormData) {
      const { initialFormData } = this.state;
      const dma_id_fk = formData.dma_id;
      const sports = formData.sports;
      const preparedData = {
        channels: formData.channels,
        selected_content: formData.selected_content || [],
        email: formData.email,
      };

      if (formData.answers.length) {
        formData.answers.forEach(answer => {
          if (answer.answer) formData[answer.slug] = answer.answer;
        });
      }

      if (dma_id_fk) {
        const live_local_news =
          formData?.live_local_news === 'yes' ? { live_local_news: 'yes' } : {};

        getChannels({ dma_id_fk });
        getTopChannels({ dma_id_fk, sports, ...live_local_news });
      }

      if (hasPrefilled) {
        store.set('start-data', formData);
      }

      this.setState({
        startData: formData,
        selectedChannels: formData.selectedChannels,
        initialFormData: { ...initialFormData, ...preparedData },
      });

      // Disable or enable next button for parent iframe
      if (isIframe) {
        sendParentMessage({ disableNext: !formData.channels.length });
      }
    }

    if (prevProps.unique_id !== unique_id && unique_id) {
      const { startData } = this.state;

      this.setState({ startData: { ...startData, ...startInitFormData, unique_id } });
    }
  }

  componentWillUnmount() {
    const { cleanFormData, isIframe, resetSkipQuestions } = this.props;
    cleanFormData();
    resetSkipQuestions();
    document.removeEventListener('keydown', this.handleKeyDown);

    if (isIframe) {
      sendParentMessage({ showParentFooter: false });
      window.removeEventListener('message', this.receiveMessage);
    }
  }

  setSelectedFilter = filter => {
    const { initialFormData } = this.state;
    let initialFormDataUpdated = initialFormData;
    if (this.form.current) {
      const { values } = this.form.current;
      initialFormDataUpdated = values;
    }

    this.setState({
      initialFormData: {
        ...initialFormDataUpdated,
        genre_search: filter?.genre_id.toString() || '',
      },
    });
  };

  getStepName = activeStep => {
    switch (activeStep) {
      case 1:
        return 'channels';
      case 2:
        return 'email';
      default:
        return '';
    }
  };

  updateSelectedChannels = data => {
    const { selectedChannels } = this.state;
    const { isIframe } = this.props;

    const isChannelsBundle = Array.isArray(data);
    let updatedData = [...selectedChannels];

    if (isChannelsBundle) {
      data.forEach(item => {
        const currentChannel = updatedData.find(channel => channel.channel_id === item.channel_id);

        if (!currentChannel) {
          updatedData.push(item);
        }
      });
    } else {
      const currentChannel = updatedData.find(channel => channel.channel_id === data.channel_id);

      if (currentChannel) {
        updatedData = updatedData.filter(channel => channel.channel_id !== data.channel_id);
      } else {
        updatedData.push(data);
      }
    }

    this.setState({ selectedChannels: updatedData });

    // Disable or enable next button for parent iframe
    if (isIframe) {
      sendParentMessage({ disableNext: !updatedData.length });
    }
  };

  handleNextStep = () => {
    if (!this.form.current) {
      return;
    }

    const { activeStep, selectedChannels } = this.state;
    const { counties, cleanCountiesState, isIframe } = this.props;
    const { values } = this.form.current;

    if (activeStep === 1) {
      if (isIframe) {
        sendParentMessage({
          showParentFooter: false,
        });
      }

      try {
        store.set('channels-data', {
          channels: values.channels,
          selectedChannels,
          selected_content: values.selected_content,
        });
      } catch (e) {
        bugsnag.notify(new Error('Local storage (channels-data)', { cause: e }));
      }
    }

    this.setState(prevState => ({ activeStep: prevState.activeStep + 1 }));

    if (counties) {
      cleanCountiesState();
    }

    if (isIframe) {
      sendParentMessage({ goToTop: true });
    }
  };

  handleBackStep = () => {
    const { activeStep, startData, isEditChannels } = this.state;
    const {
      router: { navigate },
      counties,
      cleanCountiesState,
      isIframe,
    } = this.props;

    if (isEditChannels) return;

    if (counties) {
      cleanCountiesState();
    }

    if (activeStep === 1) {
      let prevPath = startData.prevPath || ROUTE_START;

      prevPath = prevPath.includes('?') ? `${prevPath}&prev` : `${prevPath}?prev`;

      const routeEnhanced = routeEnhance(prevPath, {
        state: { location: LOCATIONS_CHANNELS_PAGE },
      });

      analyticsInternalLinks({
        unique_id: routeEnhanced.state.unique_id,
        from_page_path: routeEnhanced.state.from_page_path,
        from_search_params: routeEnhanced.state.from_search_params,
        to_page_path: routeEnhanced.pathname,
        to_search_params: routeEnhanced.search,
        partner_id: routeEnhanced.state.partner_id,
        location: routeEnhanced.state.location,
      });

      navigate(routeEnhanced);

      if (isIframe) {
        switch (startData?.partner?.partner_slug) {
          case 'metronet':
            sendParentMessage({ goToTop: true, scrollTarget: 'top' });
            break;
          case 'wow':
            sendParentMessage({ goToTop: true, scrollTarget: 'iframe' });
            break;
          default:
            sendParentMessage({ goToTop: true, scrollTarget: 'iframe' });
            break;
        }
      }

      return;
    }

    this.setState(prevState => ({ activeStep: prevState.activeStep - 1 }));
    document.activeElement.blur();

    if (isIframe) {
      sendParentMessage({
        goToTop: true,
        fullHeight: true,
        showParentFooter: startData?.partner?.settings?.show_iframe_footer,
      });
    }
  };

  handleChannelRemove = (data, analyticsData) => {
    if (!this.form.current) {
      return;
    }

    const { isIframe } = this.props;
    const { values, setValues } = this.form.current;
    const updatedChannels = [...values.channels].filter(channel => +channel !== data.channel_id);
    const selected_content = [...values.selected_content].filter(
      item => item.channel_id !== data.channel_id,
    );

    setValues({ ...values, channels: updatedChannels, selected_content });
    this.updateSelectedChannels(data, analyticsData);

    // Disable or enable next button for parent iframe
    if (isIframe) {
      sendParentMessage({ disableNext: !updatedChannels.length });
    }
  };

  handleUnselectAll = () => {
    if (!this.form.current) {
      return;
    }

    const { isIframe } = this.props;
    const { values, setValues } = this.form.current;

    setValues({ ...values, channels: [], selected_content: [] });
    this.setState({ selectedChannels: [] });

    if (isIframe) {
      sendParentMessage({ disableNext: true });
    }
  };

  handleUpdateZipCode = zipcode => {
    this.setState(prevState => ({ startData: { ...prevState.startData, zipcode } }));
  };

  handleKeyDown = event => {
    if (!this.form.current) {
      return;
    }

    const { activeStep, isEditChannels } = this.state;
    const { isValid, handleSubmit, values } = this.form.current;

    if (event.keyCode === 13 && event.srcElement) {
      if (event.srcElement.tagName === 'INPUT' || event.srcElement.tagName === 'BUTTON') return;
    }

    if (event.keyCode === 13 && isValid && values.channels.length) {
      if (activeStep === 2 || isEditChannels) {
        handleSubmit();
      } else {
        this.handleNextStep();
      }
    }
  };

  receiveMessage = event => {
    if (!this.form.current) {
      return;
    }

    const action = event.data?.action;
    const { isValid, handleSubmit, values } = this.form.current;
    const { activeStep, isEditChannels } = this.state;

    if (action === 'previous' && !isEditChannels) {
      this.handleBackStep();
    } else if (action === 'next' && isValid && values.channels.length) {
      if (activeStep === 2 || isEditChannels) {
        handleSubmit();
      } else {
        this.handleNextStep();
      }
    }
  };

  handleSubmit = (values, { setSubmitting }) => {
    const { startData } = this.state;
    const {
      submitForm,
      router: { navigate },
    } = this.props;
    const zipcode = startData.zipcode || values.zipcode;
    const dma_id = startData.dma_id || values.dma_id;
    const county_id_fk = startData.county_id_fk || values.county_id_fk;

    submitForm({ ...startData, ...values, zipcode, dma_id, county_id_fk }, () => {
      const routeEnhanced = routeEnhance(ROUTE_RESULTS, {
        state: { location: LOCATIONS_CHANNELS_PAGE },
      });

      analyticsInternalLinks({
        unique_id: routeEnhanced.state.unique_id,
        from_page_path: routeEnhanced.state.from_page_path,
        from_search_params: routeEnhanced.state.from_search_params,
        to_page_path: routeEnhanced.pathname,
        to_search_params: routeEnhanced.search,
        partner_id: routeEnhanced.state.partner_id,
        location: routeEnhanced.state.location,
      });

      navigate(routeEnhanced);
    });
    setSubmitting(false);
  };

  handleValidate = (values, validationSchema) => {
    const { questionEmailRequired } = this.props;

    try {
      validateYupSchema(values, validationSchema, true, {
        questionEmailRequired,
      });
    } catch (err) {
      return yupToFormErrors(err);
    }

    return {};
  };

  render() {
    const {
      activeStep,
      startData,
      initialFormData,
      selectedChannels,
      isEditChannels,
      redirectToStart,
    } = this.state;
    const {
      t,
      channelsLoading,
      submitLoading,
      isIframe,
      partner,
      questionEmailRequired,
      pageContent,
      unique_id,
      partnerLoading,
      skipQuestions,
    } = this.props;
    const hasStartData = !!Object.keys(startData).length;
    const skipEmailQuestion = skipQuestions.includes('email');
    const validationSchema = setYupValidationSchema(
      'channelsSchema',
      t,
      this.getStepName(activeStep),
    );

    let containerClasses = 'wizard-form';

    if (partner?.slug) {
      containerClasses += ` partner-${partner.slug} channelspage`;
    }

    if (isIframe) {
      containerClasses += ' wizard-form--scroll';
    }

    if (partner?.settings?.show_iframe_footer) {
      containerClasses += ' wizard-form--iframe-footer';
    }

    if (redirectToStart) {
      return <Redirect to={ROUTE_START} />;
    }

    const showNextBtn = activeStep < 2 && !isEditChannels && !skipEmailQuestion;
    const showSubmitBtn = activeStep === 2 || isEditChannels || skipEmailQuestion;
    const channelsQuestionsCount = skipEmailQuestion ? 1 : 2;

    return (
      <Layout>
        {!partnerLoading && (
          <Helmet onChangeClientState={analyticsPageLoaded} titleTemplate="%s | MyBundle">
            <title>Channels</title>
            <meta name="ROBOTS" content="NOINDEX, NOFOLLOW" />
          </Helmet>
        )}
        <Formik
          innerRef={this.form}
          initialValues={initialFormData}
          validate={values => this.handleValidate(values, validationSchema)}
          onSubmit={this.handleSubmit}
          enableReinitialize
          validateOnMount
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setValues,
            isValid,
          }) => {
            const footer = (
              <FooterWizard
                type="channels"
                pageName={`channels-step${activeStep}`}
                pageContent={pageContent}
                showPrev={!isEditChannels}
                showNext={showNextBtn}
                showSubmitBtn={showSubmitBtn}
                onNextPress={this.handleNextStep}
                onPrevPress={this.handleBackStep}
                onSubmit={handleSubmit}
                prevBtnProps={{ disabled: channelsLoading }}
                nextBtnProps={{
                  disabled: !isValid || channelsLoading || !values.channels.length,
                }}
                submitBtnProps={{
                  disabled: !isValid || submitLoading || isSubmitting,
                  tooltip:
                    (!isValid &&
                      activeStep === 2 &&
                      (errors.email || t('channels.email.tooltip'))) ||
                    '',
                }}
                withShadow={activeStep === 1}
                channels={activeStep === 1 ? selectedChannels : []}
                onChannelRemove={this.handleChannelRemove}
                onUnselectAll={this.handleUnselectAll}
                partner={startData.partner || {}}
                hiddenProps={{ smDown: false }}
                isIframe={isIframe}
              />
            );

            const navButtons = React.cloneElement(
              footer,
              { type: 'only-buttons', hiddenProps: { smDown: false } },
              null,
            );

            return (
              <div className={containerClasses}>
                <HeaderWizard
                  type="channels"
                  pageName={`channels-step${activeStep}`}
                  hideSteppers={isEditChannels}
                  questionsCount={startData.questionsCount}
                  questionStepperProps={{
                    active: hasStartData,
                    current: hasStartData ? startData.questionsCount : 0,
                  }}
                  channelQuestionsCount={channelsQuestionsCount}
                  channelStepperProps={{ active: true, current: activeStep }}
                  showPrev={!isEditChannels}
                  showNext={showNextBtn}
                  onNextPress={this.handleNextStep}
                  onPrevPress={this.handleBackStep}
                  nextBtnProps={{
                    disabled: !isValid || channelsLoading || !values.channels.length,
                  }}
                  prevBtnProps={{ disabled: channelsLoading }}
                  showSubmitBtn={showSubmitBtn}
                  onSubmit={handleSubmit}
                  submitBtnProps={{ disabled: !isValid || submitLoading || isSubmitting }}
                  partner={partner || startData.partner || {}}
                  location={LOCATIONS_CHANNELS_PAGE}
                />
                {isIframe && !isEditChannels && (
                  <Stepper
                    className="stepper--iframe-progress"
                    count={(startData.questionsCount || 0) + channelsQuestionsCount}
                    active
                    current={activeStep + (startData.questionsCount || 0)}
                  />
                )}
                <Form onSubmit={handleSubmit}>
                  {activeStep === 1 && (
                    <ChannelsStep
                      values={values}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      setValues={setValues}
                      startData={startData}
                      onChannelSelect={this.updateSelectedChannels}
                      onChangeZipCode={this.handleUpdateZipCode}
                      partner={startData.partner || {}}
                      setSelectedFilter={this.setSelectedFilter}
                      navButtons={navButtons}
                    />
                  )}
                  {activeStep === 2 && (
                    <Email
                      isOptional={
                        !!startData?.partner?.settings?.show_optional && !questionEmailRequired
                      }
                      values={values}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      errors={errors}
                      touched={touched}
                      partner={startData.partner || {}}
                    />
                  )}
                </Form>
                {footer}
              </div>
            );
          }}
        </Formik>
        <Guard iframe={false} partner={{ use_fmb_leave_modal: true }}>
          <DialogExitPopup
            title={t('exitPopupDialog.title')}
            subtitle={t('exitPopupDialog.subtitle')}
            buttonConfirmTitle={t('buttons.exploreMarketplace')}
            maxWidth="sm"
            allowedRoutes={[ROUTE_START, ROUTE_RESULTS]}
            confirmRoute={ROUTE_DISCOVER}
            partnerName={partner?.partner_name}
            analyticsId={unique_id}
          />
        </Guard>
      </Layout>
    );
  }
}

Channels.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  filters: PropTypes.array.isRequired,
  channelsLoading: PropTypes.bool.isRequired,
  getChannelFilters: PropTypes.func.isRequired,
  getChannels: PropTypes.func.isRequired,
  getTopChannels: PropTypes.func.isRequired,
  getPagesContent: PropTypes.func.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  submitForm: PropTypes.func.isRequired,
  getFormData: PropTypes.func.isRequired,
  formData: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  formDataLoading: PropTypes.bool.isRequired,
  cleanCountiesState: PropTypes.func.isRequired,
  counties: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  countiesLoading: PropTypes.bool.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
  isIframe: PropTypes.bool.isRequired,
  urlPartner: PropTypes.string.isRequired,
  getUniqueId: PropTypes.func.isRequired,
  cleanFormData: PropTypes.func.isRequired,
  skipQuestions: PropTypes.array.isRequired,
  isPartnerDomain: PropTypes.bool.isRequired,
  partner: PropTypes.object.isRequired,
  unique_id: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  getSkipQuestions: PropTypes.func.isRequired,
  resetSkipQuestions: PropTypes.func.isRequired,
  questionEmailRequired: PropTypes.bool.isRequired,
  pageContent: PropTypes.object.isRequired,
  handlePrefetch: PropTypes.func.isRequired,
};

const mapStateToProps = ({ app, auth, channels, counties, wizard, partner, pages }) => ({
  isIframe: app.isIframe,
  urlPartner: app.partner,
  user: auth.user,
  filters: channels.filters,
  channelsLoading: channels.loadings.channels,
  submitLoading: channels.loadings.submitForm,
  formData: channels.formData,
  formDataLoading: channels.loadings.formData,
  counties: counties.counties,
  countiesLoading: counties.loading,
  isPartnerDomain: partner.isPartnerDomain,
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
  unique_id: wizard.unique_id,
  questionEmailRequired: wizard.question_email_required,
  skipQuestions: wizard.skipQuestions,
  pageContent: pages.content,
});

const mapDispatchToProps = dispatch => {
  return {
    getSkipQuestions: slug => dispatch(WizardActions.getSkipQuestions.action({ slug })),
    resetSkipQuestions: () => dispatch(WizardActions.resetSkipQuestions.action()),
    getChannelFilters: () => dispatch(ChannelsActions.getChannelFilters.action()),
    getChannels: data => dispatch(ChannelsActions.getChannels.action({ data })),
    getTopChannels: data => dispatch(ChannelsActions.getTopChannels.action({ data })),
    submitForm: (data, callback) => dispatch(ChannelsActions.submitForm.action({ data, callback })),
    getFormData: unique_id => dispatch(ChannelsActions.getFormData.action({ unique_id })),
    cleanCountiesState: () => dispatch(CountiesActions.cleanCountiesState.action()),
    getUniqueId: (slug, form_version) => {
      dispatch(WizardActions.getUniqueId.action({ slug, form_version }));
    },
    cleanFormData: () => dispatch(ChannelsActions.cleanFormData.action()),
    getPagesContent: pageSlug => dispatch(PagesActions.getPagesContent.action({ pageSlug })),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withPrefetch(),
  withRouter,
)(Channels);
